import {
  getCommonFacet,
  SCRIBE_DELIVERY_DETAILS_EVENTS,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_SHOPIFY_PARAMS,
  SCRIBE_STATE_NAMES,
  scribeTrackEvent,
  trackEVRegulationEvent,
} from 'applications/scribe/instrumentations/track';
import ScribeDuck from 'ducks/scribe-duck.js';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { getDeliveryID } from 'helper/order';
import { getSelectedShopifyShop } from 'helper/shopify';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';
import { selectedCitySelector, userInfoSelector } from 'selectors/app-selector';
import {
  creatingDeliveriesSelector,
  deliveryDetailSelector,
  deliverySelector,
} from 'selectors/new-booking-selectors';
import { ongoingDeliveriesV2Selector } from 'selectors/new-history-selectors';
import { shopifyShopsSelector } from 'selectors/shopify-selectors';
import { MODE } from 'views/v2/home/constants';
import {
  deliveryFormFields,
  isStepDetailField,
  makeStepDetailFieldName,
  parseStepDetailField,
} from 'views/v2/new-booking-page/delivery-form/common';
import { isAppliedOffer } from './helper/scribe-helper';

export const getPickUpAndDropOffPoiFromDelivery = deliveryDetail => {
  if (!deliveryDetail) {
    return {
      pickUpLocationSource: null,
      pickUpLocationId: null,
      dropOffLocationIds: [],
    };
  }

  const pickUpLocationSource = _get(deliveryDetail, 'pickUp.source', null);
  const pickUpLocationId = _get(deliveryDetail, 'pickUp.poi.id', null);
  const dropOffs = _get(deliveryDetail, 'dropOffs', []).filter(
    dropOff => dropOff && dropOff.poi,
  );

  if (_isEmpty(dropOffs)) {
    return {
      pickUpLocationSource,
      pickUpLocationId,
      dropOffLocationIds: [],
    };
  }
  const dropOffLocationIds = dropOffs.map(dropOff => _get(dropOff, 'poi.id'));
  return {
    pickUpLocationSource,
    pickUpLocationId,
    dropOffLocationIds: [...dropOffLocationIds],
  };
};

const mapEventFromFields = ({ fieldName, value, isPickUp, phoneNameValue }) => {
  switch (fieldName) {
    case deliveryFormFields.phone.fieldName:
      return {
        eventName: isPickUp
          ? SCRIBE_DELIVERY_DETAILS_EVENTS.SENDER_PHONE
          : SCRIBE_DELIVERY_DETAILS_EVENTS.RECIPIENT_PHONE,
        additionalFacet: {
          [isPickUp
            ? SCRIBE_EVENT_PARAMS.SENDER_PHONE_TEXT
            : SCRIBE_EVENT_PARAMS.RECIPIENT_PHONE_TEXT]: value,
          [isPickUp
            ? SCRIBE_EVENT_PARAMS.SENDER_NAME_TEXT
            : SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT]: phoneNameValue,
        },
      };
    case deliveryFormFields.name.fieldName:
      return {
        eventName: isPickUp
          ? SCRIBE_DELIVERY_DETAILS_EVENTS.SENDER_NAME
          : SCRIBE_DELIVERY_DETAILS_EVENTS.RECIPIENT_NAME,
        additionalFacet: {
          [isPickUp
            ? SCRIBE_EVENT_PARAMS.SENDER_NAME_TEXT
            : SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT]: value,
        },
      };
    case deliveryFormFields.vehicleType.fieldName:
      return {
        eventName: [SCRIBE_DELIVERY_DETAILS_EVENTS.SERVICE_TYPE],
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: value,
        },
      };
    case deliveryFormFields.itemWeight.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.ITEM_WEIGHT,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.ITEM_WEIGHT]: value,
        },
      };
    case deliveryFormFields.itemType.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.ITEM_CATEGORY,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.CATEGORY_NAME]: value,
        },
      };
    case deliveryFormFields.locationNote.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DRIVER_NOTES,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.TEXT]: value,
        },
      };
    case deliveryFormFields.paymentTypeID.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.PAYMENT_TYPE,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.PAYMENT_METHOD]: value,
        },
      };
    case deliveryFormFields.referenceCode.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.REF_CODE,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.TEXT]: value,
        },
      };
    case deliveryFormFields.deliveryInstruction.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DRIVER_NOTES,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.TEXT]: value,
        },
      };
    case deliveryFormFields.promoCode.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.PROMO_CODE,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.PROMO_CODE]: value,
        },
      };
    case deliveryFormFields.cod.fieldName:
      return {
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.COD_VALUE,
        additionalFacet: {
          [SCRIBE_EVENT_PARAMS.COD_AMOUNT]: value,
        },
      };
    default:
      return {
        eventName: null,
        additionalFacet: null,
      };
  }
};

const scribeTrackBookOrderForm = ({
  changedFields,
  allValues,
  userInfo,
  index,
}) => {
  Object.entries(changedFields).forEach(([key, value]) => {
    let eventName, additionalFacet, nameTextFieldValue;
    if (isStepDetailField(key)) {
      const { isPickUp, index, fieldName } = parseStepDetailField(key);

      if (fieldName === deliveryFormFields.phone.fieldName) {
        nameTextFieldValue = makeStepDetailFieldName(
          deliveryFormFields.name.fieldName,
          isPickUp,
          index || 0,
        );
        const phoneNameValue = _get(allValues, `${nameTextFieldValue}.value`);

        ({ eventName, additionalFacet } = mapEventFromFields({
          fieldName,
          value: _get(value, 'value', null),
          isPickUp,
          phoneNameValue,
        }));
      } else {
        ({ eventName, additionalFacet } = mapEventFromFields({
          fieldName,
          value: _get(value, 'value', null),
          isPickUp,
        }));
      }
    } else {
      ({ eventName, additionalFacet } = mapEventFromFields({
        fieldName: key,
        value: _get(value, 'value', null),
      }));
    }

    if (!_isEmpty(eventName)) {
      const facets = {
        ...getCommonFacet(userInfo),
        ...additionalFacet,
      };
      if (!_isEmpty(index)) {
        facets[SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER] = index;
      }
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
        eventName,
        facets,
      });
    }
  });
};

const scribeExpressDeliveryDetailFormChangeEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_FORM_CHANGE),
    debounceTime(500),
    mergeMap(action => {
      const { payload } = action;
      const userInfo = userInfoSelector(state$.value);
      const creatingDeliveries = creatingDeliveriesSelector(state$.value);
      const { changedFields, allValues, index } = payload;

      if (!creatingDeliveries) {
        scribeTrackBookOrderForm({
          changedFields,
          allValues,
          userInfo,
          index,
        });
      }

      return EMPTY;
    }),
  );

const scribeExpressHomePageWebServiceTypeEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_SERVICE_TYPE),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBulkUploadReviewDefaultEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BULK_UPLOAD_REVIEW_DEFAULT),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        uploadTotal,
        batchRefs,
        bookingNumbers,
      } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_TOTAL]: uploadTotal,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_REFERENCE_CODE]: batchRefs,
          [SCRIBE_EVENT_PARAMS.BULK_BOOKING_NUMBER]: bookingNumbers,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBulkUploadReviewEditAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BULK_UPLOAD_EDIT_ADDRESS),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        originalAddress,
        adjustedToAddress,
        isSender,
        orderBatchRef,
      } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.STEP_TYPE]: isSender ? 'PICKUP' : 'DROPOFF',
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_REFERENCE_CODE]: orderBatchRef,
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ORIGINAL]: originalAddress,
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: adjustedToAddress,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBulkUploadEditAddressWebAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_EDIT_ADDRESS_WEB_ADDRESS),
    mergeMap(action => {
      const { stateName, eventName, adjustedToAddress } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: adjustedToAddress,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressHomePageWebPickupAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_PICKUP_ADDRESS),
    mergeMap(action => {
      const { stateName, eventName, deliveryDetail } = action;

      const {
        pickUpLocationSource,
        pickUpLocationId,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);
      const userInfo = userInfoSelector(state$.value);
      const facets = getCommonFacet(userInfo);
      facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_ID] = pickUpLocationId;
      facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_SRC] = pickUpLocationSource;
      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressHomePageWebDropoffAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_DROPOFF_ADDRESS),
    mergeMap(action => {
      const { stateName, eventName, deliveryDetail } = action;

      const {
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      const dropOffLocationId = dropOffLocationIds[0];
      const userInfo = userInfoSelector(state$.value);
      const facets = getCommonFacet(userInfo);

      if (pickUpLocationId) {
        facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_ID] = pickUpLocationId;
      }

      if (dropOffLocationId) {
        facets[SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID] = dropOffLocationId;
      }

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });

      return EMPTY;
    }),
  );

const scribeExpressHomePageSwapDeliveryPickUpDropOffEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_SWAP),
    mergeMap(action => {
      const { stateName, eventName, deliveryDetail } = action;
      const {
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      if (!_isEmpty(dropOffLocationIds) || pickUpLocationId) {
        const dropOffLocationId = dropOffLocationIds[0];
        const userInfo = userInfoSelector(state$.value);

        const facets = getCommonFacet(userInfo);

        if (pickUpLocationId) {
          facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_ID] = pickUpLocationId;
        }

        if (dropOffLocationId) {
          facets[SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID] = dropOffLocationId;
        }

        scribeTrackEvent({
          stateName,
          eventName,
          facets,
        });
      }
      return EMPTY;
    }),
  );

const scribeExpressHomePageWebConfirmBookingEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_CONFIRM_BOOKING),
    mergeMap(action => {
      const { stateName, eventName, deliveryDetail } = action;
      const {
        pickUpLocationSource,
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: MODE.MULTI_BOOK,
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_SRC]: pickUpLocationSource,
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: pickUpLocationId,
          [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: [...dropOffLocationIds],
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressHomePageWebOngoingOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_ONGOING_ORDER),
    mergeMap(action => {
      const { stateName, eventName } = action;

      const userInfo = userInfoSelector(state$.value);
      const { deliveries } = ongoingDeliveriesV2Selector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ONGOING_BOOKINGS]: deliveries.length,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressHomePageWebRecentOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_RECENT_ORDER),
    mergeMap(action => {
      const { stateName, eventName } = action;

      const userInfo = userInfoSelector(state$.value);
      const { deliveries } = ongoingDeliveriesV2Selector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ORDER_ID]: deliveries
            .slice(0, 2)
            .map(o => o.deliveryID),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressHomePageMultipleBookingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_MULTI_BOOK),
    mergeMap(action => {
      const { stateName, eventName } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: MODE.MULTI_BOOK,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressSavedPOISelectPOIFromDeliverToEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_HOMEPAGE_WEB_SELECT_POI_FROM_DELIVER_TO),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        deliverToIndex,
        savedPoiNumber,
        favoritesPredictFlag,
      } = action;
      const userInfo = userInfoSelector(state$.value);

      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVER_TO_INDEX]: deliverToIndex,
          [SCRIBE_EVENT_PARAMS.SAVE_POI_NUMBER]: savedPoiNumber,
          [SCRIBE_EVENT_PARAMS.FAVORITE_PREDICT_FLAG]: favoritesPredictFlag,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebBookAgainEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_BOOK_AGAIN),
    mergeMap(action => {
      const { stateName, eventName, order } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ORDER_ID]: getDeliveryID(order),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebPrintLabelEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_PRINT_LABEL),
    mergeMap(action => {
      const { stateName, eventName } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebBulkPrintLabelEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_BULK_PRINT_LABEL),
    mergeMap(action => {
      const { stateName, eventName, orderCount } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_SELECTED]: orderCount,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebShowOngoingBookingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_SHOW_ONGOING_BOOKINGS),
    mergeMap(action => {
      const { stateName, eventName, selectedPageSize } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ONGOING_BOOKINGS_FILTER]: selectedPageSize,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebOngoingBookingsFilterEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_ONGOING_BOOKINGS_FILTER),
    mergeMap(action => {
      const { stateName, eventName, orderType } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ONGOING_FILTER_NAME]: orderType,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebOngoingSectionEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_ONGOING_SECTION),
    mergeMap(action => {
      const { stateName, eventName, ongoingTotal } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ONGOING_BOOKINGS]: ongoingTotal,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebTableActionEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      ScribeDuck.types.EXPRESS_BOOKINGS_WEB_ONGOING_TABLE_ACTION,
      ScribeDuck.types.EXPRESS_BOOKINGS_WEB_HISTORY_TABLE_ACTION,
    ),
    mergeMap(action => {
      const { stateName, eventName, order } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ORDER_ID]: order.deliveryID,
          [SCRIBE_EVENT_PARAMS.BOOKING_CODE]: order.bookingCode,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebTableFilterEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      ScribeDuck.types.EXPRESS_BOOKINGS_WEB_ONGOING_BOOKINGS_STATUS,
      ScribeDuck.types.EXPRESS_BOOKINGS_WEB_HISTORY_BOOKINGS_STATUS,
      ScribeDuck.types.EXPRESS_BOOKINGS_WEB_HISTORY_BOOKINGS_DATE,
    ),
    mergeMap(action => {
      const { stateName, eventName } = action;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressBookingsWebShopifyEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_BOOKINGS_WEB_SHOPIFY),
    mergeMap(action => {
      const { stateName, eventName, facets = {} } = action;

      const userInfo = userInfoSelector(state$.value);
      const shopifyShops = shopifyShopsSelector(state$.value);

      const commonFacets = {
        ...getCommonFacet(userInfo),
      };
      const shop = getSelectedShopifyShop(shopifyShops);
      if (shop) {
        commonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_NAME] = shop.name;
        commonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID] = shop.shop;
      }

      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...commonFacets,
          ...facets,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryOptionsGenericEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_GENERIC),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDeliveryTypeEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_DELIVERY_TYPE),
    mergeMap(action => {
      const { stateName, eventName, serviceType } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: serviceType,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryVehicleTypeEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_VEHICLE_TYPE),
    mergeMap(action => {
      const { stateName, eventName, vehicleType } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: vehicleType,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailAddOnEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_ADD_ON),
    mergeMap(action => {
      const { stateName, eventName, deliveryID } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });
      const selectedServices = _get(
        deliveryDetail,
        'configuration.additionalServices',
        [],
      );

      if (!_isEmpty(selectedServices)) {
        scribeTrackEvent({
          stateName,
          eventName,
          facets: {
            ...getCommonFacet(userInfo),
            [SCRIBE_EVENT_PARAMS.ADD_ON_LIST]: selectedServices,
          },
        });
      }

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailDeleteAddOnEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_DELETE_ADD_ON),
    mergeMap(action => {
      const { stateName, eventName, deliveryID } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });

      const selectedServices = _get(
        deliveryDetail,
        'configuration.additionalServices',
        [],
      );

      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ADD_ON_LIST]: selectedServices,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliverySelectPickupDateEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_PICKUP_DATE),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliverySelectPickupTimeEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_PICKUP_TIME),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryConfirmEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_OPTIONS_CONFIRM),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressPOISearchSelectPOIFromResultEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_SELECT_POI_FROM_RESULT),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        poiType,
        keyword,
        selectedPOI,
        selectedIndex,
        candidatePOIs,
        version,
        isRecommendedPoi,
        poiCategory,
        poiLabel,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      if (!_isEmpty(selectedPOI)) {
        scribeTrackEvent({
          stateName,
          eventName,
          facets: {
            ...getCommonFacet(userInfo),
            [SCRIBE_EVENT_PARAMS.CHARACTER_TYPED]: keyword || null,
            [SCRIBE_EVENT_PARAMS.POI_TYPE]: poiType,
            [SCRIBE_EVENT_PARAMS.POI_ID]: selectedPOI,
            [SCRIBE_EVENT_PARAMS.SELECT_POI_INDEX]: selectedIndex,
            [SCRIBE_EVENT_PARAMS.RECOMMEND_POI_LIST]: candidatePOIs,
            [SCRIBE_EVENT_PARAMS.VERSION]: version,
            [SCRIBE_EVENT_PARAMS.IS_RECOMMENDED_POI]: !!isRecommendedPoi,
            [SCRIBE_EVENT_PARAMS.POI_CATEGORY]: poiCategory || null,
            [SCRIBE_EVENT_PARAMS.POI_LABEL]: poiLabel || null,
          },
        });
      }

      return EMPTY;
    }),
  );

const scribeExpressPOISearchMapSearchResultEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_MAP_SEARCH_RESULT),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        latitude,
        longitude,
        poiType,
        poiID,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.LATITUDE]: latitude,
          [SCRIBE_EVENT_PARAMS.LONGITUDE]: longitude,
          [SCRIBE_EVENT_PARAMS.POI_TYPE]: poiType,
          [SCRIBE_EVENT_PARAMS.POI_ID]: poiID,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressPOIRouteOptimizationEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_SELECT_POI_FIND_SHORTEST_ROUTE,
      ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_SELECT_POI_USE_ORIGINAL_ROUTE,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_FIND_SHORTEST_ROUTE,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_USE_ORIGINAL_ROUTE,
    ),
    mergeMap(action => {
      const { stateName, eventName, deliveryID } = action;

      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });
      const {
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      if (pickUpLocationId && !_isEmpty(dropOffLocationIds)) {
        scribeTrackEvent({
          stateName,
          eventName,
          facets: {
            ...getCommonFacet(userInfo),
            [SCRIBE_EVENT_PARAMS.DELIVERY_UUID]: deliveryID,
            [SCRIBE_EVENT_PARAMS.STEP_TYPE]: 'DROPOFF',
            [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: pickUpLocationId,
            [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: dropOffLocationIds,
          },
        });
      }

      return EMPTY;
    }),
  );

const scribeExpressPOIFindShortestRouteResultsEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      ScribeDuck.types
        .EXPRESS_POI_SEARCH_WEB_SELECT_POI_FIND_SHORTEST_ROUTE_RESULTS,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_FIND_SHORTEST_ROUTE_RESULTS,
    ),
    mergeMap(action => {
      const { stateName, eventName, deliveryID, status, forceTrack } = action;
      const delivery = deliverySelector(state$.value, {
        deliveryID,
      });
      // don't track when it is not enabled or already applied routeOptimization (second quote call)
      if (
        !forceTrack &&
        (!_get(delivery, 'routeOptimization.enabled') ||
          _get(delivery, 'routeOptimization.optimal'))
      ) {
        return EMPTY;
      }

      const userInfo = userInfoSelector(state$.value);
      const {
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(delivery.detail);

      if (pickUpLocationId && !_isEmpty(dropOffLocationIds)) {
        scribeTrackEvent({
          stateName,
          eventName,
          facets: {
            ...getCommonFacet(userInfo),
            [SCRIBE_EVENT_PARAMS.DELIVERY_UUID]: deliveryID,
            [SCRIBE_EVENT_PARAMS.STEP_TYPE]: 'DROPOFF',
            [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: pickUpLocationId,
            [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: dropOffLocationIds,
            [SCRIBE_EVENT_PARAMS.ROUTE_OPTIMIZE_STATUS]: status,
          },
        });
      }

      return EMPTY;
    }),
  );

const scribeExpressPOISearchWebAddStopEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_ADD_STOP),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressPOISearchWebConfirmEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_POI_SEARCH_WEB_CONFIRM),
    mergeMap(action => {
      const { stateName, eventName, deliveryID, isPickUp } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });

      const {
        pickUpLocationSource,
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      const facets = getCommonFacet(userInfo);

      if (isPickUp) {
        facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_SRC] = pickUpLocationSource;
        facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_ID] = pickUpLocationId;
      } else {
        facets[SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID] = dropOffLocationIds;
      }

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailDuplicateDeliveryEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_DUPLICATE_DELIVERY),
    mergeMap(action => {
      const { stateName, eventName, index } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailEditDeliveryEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_EDIT_DELIVERY),
    mergeMap(action => {
      const { stateName, eventName, deliveryID } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });
      const { dropOffLocationIds } = getPickUpAndDropOffPoiFromDelivery(
        deliveryDetail,
      );
      const facets = getCommonFacet(userInfo);

      if (!_isEmpty(dropOffLocationIds)) {
        facets[SCRIBE_EVENT_PARAMS.COUNT_DROPOFFS] = dropOffLocationIds.length;
      }
      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailPickUpAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_PICKUP_ADDRESS),
    mergeMap(action => {
      const { stateName, eventName, index, deliveryID } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });

      const {
        pickUpLocationSource,
        pickUpLocationId,
      } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);

      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: pickUpLocationId,
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_SRC]: pickUpLocationSource,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailDropOffAddressEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_DROPOFF_ADDRESS),
    mergeMap(action => {
      const { stateName, eventName, index, deliveryID } = action;
      const userInfo = userInfoSelector(state$.value);
      const deliveryDetail = deliveryDetailSelector(state$.value, {
        deliveryID,
      });

      const { dropOffLocationIds } = getPickUpAndDropOffPoiFromDelivery(
        deliveryDetail,
      );

      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
          [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: dropOffLocationIds,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailAddDeliveryLeftEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_ADD_DELIVERY_LEFT),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailAddDeliveryBottomEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_ADD_DELIVERY_BOTTOM),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailLeftBarEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_LEFT_BAR),
    mergeMap(action => {
      const { stateName, eventName, index } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailDeleteDeliveryEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_DELETE_DELIVERY),
    mergeMap(action => {
      const { stateName, eventName, index } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
        },
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailBookEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_BOOK),
    mergeMap(action => {
      const { stateName, eventName, createDeliveryIDs } = action;
      const userInfo = userInfoSelector(state$.value);

      const facets = {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: [],
        [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: [],
        [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: [],
        [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: [],
        [SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT]: [],
        [SCRIBE_EVENT_PARAMS.RECIPIENT_PHONE_TEXT]: [],
        [SCRIBE_EVENT_PARAMS.COD_AMOUNT]: [],
        [SCRIBE_EVENT_PARAMS.ITEM_WEIGHT]: [],
        [SCRIBE_EVENT_PARAMS.CATEGORY_NAME]: [],
        [SCRIBE_EVENT_PARAMS.TEXT]: [],
        [SCRIBE_EVENT_PARAMS.PAYMENT_METHOD]: [],
        [SCRIBE_EVENT_PARAMS.FARE]: [],
        [SCRIBE_EVENT_PARAMS.CURRENCY]: [],
      };

      createDeliveryIDs.forEach(deliveryID => {
        const delivery = deliverySelector(state$.value, {
          deliveryID,
        });

        const deliveryDetail = _get(delivery, 'detail');

        const {
          pickUpLocationId,
          dropOffLocationIds,
        } = getPickUpAndDropOffPoiFromDelivery(deliveryDetail);
        facets[SCRIBE_EVENT_PARAMS.PICKUP_POI_ID].push(pickUpLocationId);
        facets[SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID].push(dropOffLocationIds);
        facets[SCRIBE_EVENT_PARAMS.SERVICE_TYPE].push(
          _get(deliveryDetail, 'configuration.serviceType'),
        );
        facets[SCRIBE_EVENT_PARAMS.TAXI_TYPE].push(
          _get(deliveryDetail, 'configuration.vehicleType'),
        );

        facets[SCRIBE_EVENT_PARAMS.ITEM_WEIGHT].push(
          _get(deliveryDetail, 'itemWeight'),
        );
        facets[SCRIBE_EVENT_PARAMS.CATEGORY_NAME].push(
          _get(deliveryDetail, 'itemType'),
        );
        facets[SCRIBE_EVENT_PARAMS.TEXT].push(
          _get(deliveryDetail, 'deliveryInstruction'),
        );
        facets[SCRIBE_EVENT_PARAMS.PAYMENT_METHOD].push(
          _get(deliveryDetail, 'paymentTypeID'),
        );
        facets[SCRIBE_EVENT_PARAMS.FARE].push(
          _get(delivery, 'quote.finalFare.lowerBound', 0),
        );
        facets[SCRIBE_EVENT_PARAMS.CURRENCY].push(
          _get(delivery, 'quote.currency.code'),
        );

        _get(deliveryDetail, 'dropOffs', []).forEach(dropOff => {
          facets[SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT].push(
            _get(dropOff, 'name'),
          );
          facets[SCRIBE_EVENT_PARAMS.RECIPIENT_PHONE_TEXT].push(
            _get(dropOff, 'phone'),
          );
          facets[SCRIBE_EVENT_PARAMS.COD_AMOUNT].push(_get(dropOff, 'cod'));
        });
      });

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });

      return EMPTY;
    }),
  );

const scribeExpressDeliveryDetailApplyOfferEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_DELIVERY_DETAILS_APPLY_OFFER),
    mergeMap(action => {
      const { stateName, eventName, index } = action;
      const userInfo = userInfoSelector(state$.value);
      const city = selectedCitySelector(state$.value);
      const facets = getCommonFacet(userInfo);

      facets[SCRIBE_EVENT_PARAMS.CITY_ID] = _get(city, 'cityID');
      facets[SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER] = index;

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressGenericCommonOfferActionEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_DEFAULT,
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_BACK,
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_ENTER_OFFER_CODE,
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_CONTINUE_WITHOUT_OFFER,
      ScribeDuck.types.EXPRESS_OFFER_DETAILS_USE_OFFER,
      ScribeDuck.types.EXPRESS_ENTER_CODE_DETAILS_BACK,
      ScribeDuck.types.EXPRESS_ENTER_CODE_DETAILS_TYPE_CODE,
      ScribeDuck.types.EXPRESS_ENTER_CODE_DETAILS_SHOW_ERROR_RESULT,
      ScribeDuck.types.EXPRESS_ENTER_CODE_DETAILS_SELECT_OFFER,
      ScribeDuck.types.EXPRESS_ENTER_CODE_DETAILS_APPLY_OFFER,
    ),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        selectedOffer,
        typeCode,
        codeError,
        codeUsed,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      const city = selectedCitySelector(state$.value);
      const facets = getCommonFacet(userInfo);

      facets[SCRIBE_EVENT_PARAMS.CITY_ID] = _get(city, 'cityID');
      facets[SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER] = isAppliedOffer(
        selectedOffer,
      );

      if (typeCode !== undefined) {
        facets[SCRIBE_EVENT_PARAMS.TYPE_CODE] = typeCode;
      }

      if (codeError !== undefined) {
        facets[SCRIBE_EVENT_PARAMS.IS_CODE_ERROR] = codeError;
      }

      if (codeUsed !== undefined) {
        facets[SCRIBE_EVENT_PARAMS.IS_CODE_USED] = codeUsed;
      }

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressReviewOfferListCommonOfferInteractionEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_CHECK_OFFER_DETAILS,
      ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_SELECT_OFFER,
      ScribeDuck.types.EXPRESS_OFFER_DETAILS_BACK,
      ScribeDuck.types.EXPRESS_OFFER_DETAILS_DEFAULT,
    ),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        selectedOffer,
        currentOfferID,
        appliedOfferResult,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      const city = selectedCitySelector(state$.value);
      const facets = getCommonFacet(userInfo);

      facets[SCRIBE_EVENT_PARAMS.CITY_ID] = _get(city, 'cityID');
      if (currentOfferID === _get(selectedOffer, 'offerID')) {
        facets[SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER] = isAppliedOffer(
          selectedOffer,
        );
      } else {
        facets[SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER] = 'NONE';
      }

      if (appliedOfferResult) {
        facets[SCRIBE_EVENT_PARAMS.APPLIED_OFFER_RESULT] = appliedOfferResult;
      }

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressReviewOfferListApplyOfferEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EXPRESS_REVIEW_OFFER_LIST_APPLY_OFFER),
    mergeMap(action => {
      const { stateName, eventName, appliedOfferResult } = action;
      const userInfo = userInfoSelector(state$.value);
      const city = selectedCitySelector(state$.value);
      const facets = getCommonFacet(userInfo);

      facets[SCRIBE_EVENT_PARAMS.CITY_ID] = _get(city, 'cityID');

      if (appliedOfferResult === 'SUCCESS') {
        facets[SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER] = 'VALID';
      } else if (appliedOfferResult === 'FAILED') {
        facets[SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER] = 'NONE';
      }

      facets[SCRIBE_EVENT_PARAMS.APPLIED_OFFER_RESULT] = appliedOfferResult;

      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeEVRegulationEventEpic = (action$, state$) =>
  action$.pipe(
    ofType(ScribeDuck.types.EV_REGULATION_EVENT),
    mergeMap(action => {
      const { stateName, eventName, errorCode, title } = action;
      const userInfo = userInfoSelector(state$.value);

      trackEVRegulationEvent({
        userInfo,
        errorCode,
        title,
        stateName,
        eventName,
      });
      return EMPTY;
    }),
  );

export default combineEpics(
  scribeExpressHomePageWebServiceTypeEpic,
  scribeExpressBulkUploadReviewDefaultEpic,
  scribeExpressBulkUploadReviewEditAddressEpic,
  scribeExpressBulkUploadEditAddressWebAddressEpic,
  scribeExpressHomePageWebPickupAddressEpic,
  scribeExpressHomePageSwapDeliveryPickUpDropOffEpic,
  scribeExpressHomePageWebDropoffAddressEpic,
  scribeExpressHomePageWebConfirmBookingEpic,
  scribeExpressHomePageWebOngoingOrderEpic,
  scribeExpressHomePageWebRecentOrderEpic,
  scribeExpressHomePageMultipleBookingsEpic,
  scribeExpressBookingsWebBookAgainEpic,
  scribeExpressBookingsWebPrintLabelEpic,
  scribeExpressBookingsWebBulkPrintLabelEpic,
  scribeExpressBookingsWebShowOngoingBookingsEpic,
  scribeExpressBookingsWebOngoingBookingsFilterEpic,
  scribeExpressBookingsWebOngoingSectionEpic,
  scribeExpressBookingsWebTableFilterEpic,
  scribeExpressBookingsWebTableActionEpic,
  scribeExpressBookingsWebShopifyEpic,
  scribeExpressDeliveryOptionsGenericEpic,
  scribeExpressDeliveryDeliveryTypeEpic,
  scribeExpressDeliveryVehicleTypeEpic,
  scribeExpressDeliverySelectPickupDateEpic,
  scribeExpressDeliverySelectPickupTimeEpic,
  scribeExpressDeliveryConfirmEpic,
  scribeExpressPOISearchMapSearchResultEpic,
  scribeExpressPOISearchSelectPOIFromResultEpic,
  scribeExpressPOIRouteOptimizationEpic,
  scribeExpressPOIFindShortestRouteResultsEpic,
  scribeExpressPOISearchWebAddStopEpic,
  scribeExpressPOISearchWebConfirmEpic,
  scribeExpressDeliveryDetailFormChangeEpic,
  scribeExpressDeliveryDetailDuplicateDeliveryEpic,
  scribeExpressDeliveryDetailEditDeliveryEpic,
  scribeExpressDeliveryDetailAddOnEpic,
  scribeExpressDeliveryDetailDeleteAddOnEpic,
  scribeExpressDeliveryDetailPickUpAddressEpic,
  scribeExpressDeliveryDetailDropOffAddressEpic,
  scribeExpressDeliveryDetailAddDeliveryLeftEpic,
  scribeExpressDeliveryDetailAddDeliveryBottomEpic,
  scribeExpressDeliveryDetailLeftBarEpic,
  scribeExpressDeliveryDetailDeleteDeliveryEpic,
  scribeExpressDeliveryDetailBookEpic,
  scribeExpressDeliveryDetailApplyOfferEpic,
  scribeExpressGenericCommonOfferActionEpic,
  scribeExpressReviewOfferListCommonOfferInteractionEpic,
  scribeExpressReviewOfferListApplyOfferEpic,
  scribeExpressSavedPOISelectPOIFromDeliverToEpic,
  scribeEVRegulationEventEpic,
);
